import * as React from 'react';
import { AnimatePresence, MotionConfig, motion } from 'framer-motion';
import { cn } from '../../lib/utils';

const TRANSITION = {
  type: 'spring',
  bounce: 0.1,
  duration: 0.4,
};

const FloatingActionPanelContext = React.createContext(undefined);

function useFloatingActionPanelLogic() {
  const uniqueId = React.useId();
  const [isOpen, setIsOpen] = React.useState(false);
  const [triggerRect, setTriggerRect] = React.useState(null);
  const [title, setTitle] = React.useState('');
  const [note, setNote] = React.useState('');
  const [mode, setMode] = React.useState('actions');

  const openPanel = (rect, newMode = 'actions') => {
    setTriggerRect(rect);
    setMode(newMode);
    setIsOpen(true);
  };

  const closePanel = () => {
    setIsOpen(false);
    setNote('');
  };

  return {
    isOpen,
    openPanel,
    closePanel,
    uniqueId,
    triggerRect,
    title,
    setTitle,
    note,
    setNote,
    mode,
  };
}

export function FloatingActionPanelRoot({ children, className }) {
  const floatingPanelLogic = useFloatingActionPanelLogic();

  return (
    <FloatingActionPanelContext.Provider value={floatingPanelLogic}>
      <MotionConfig transition={TRANSITION}>
        <div className={cn('relative', className)}>{children(floatingPanelLogic)}</div>
      </MotionConfig>
    </FloatingActionPanelContext.Provider>
  );
}

export function FloatingActionPanelTrigger({ children, className, title, mode = 'actions' }) {
  const context = React.useContext(FloatingActionPanelContext);
  if (!context) throw new Error('FloatingActionPanelTrigger must be inside FloatingActionPanelRoot');
  const { openPanel, uniqueId, setTitle } = context;
  const triggerRef = React.useRef(null);

  const handleClick = () => {
    if (triggerRef.current) {
      openPanel(triggerRef.current.getBoundingClientRect(), mode);
      setTitle(title);
    }
  };

  return (
    <motion.button
      ref={triggerRef}
      layoutId={`floating-panel-trigger-${uniqueId}-${mode}`}
      className={cn(
        'flex h-9 items-center rounded-lg border border-border bg-card px-3 text-sm font-medium text-foreground shadow-sm hover:bg-muted',
        className
      )}
      onClick={handleClick}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      {children}
    </motion.button>
  );
}

export function FloatingActionPanelContent({ children, className }) {
  const context = React.useContext(FloatingActionPanelContext);
  if (!context) throw new Error('FloatingActionPanelContent must be inside FloatingActionPanelRoot');
  const { isOpen, closePanel, uniqueId, triggerRect, title, mode } = context;
  const contentRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (contentRef.current && !contentRef.current.contains(event.target)) {
        closePanel();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [closePanel]);

  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closePanel();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [closePanel]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ backdropFilter: 'blur(0px)' }}
            animate={{ backdropFilter: 'blur(4px)' }}
            exit={{ backdropFilter: 'blur(0px)' }}
            className="fixed inset-0 z-40 bg-black/5"
          />
          <motion.div
            ref={contentRef}
            layoutId={`floating-panel-${uniqueId}-${mode}`}
            className={cn(
              'fixed z-50 min-w-[200px] overflow-hidden rounded-xl border border-border bg-card shadow-lg',
              className
            )}
            style={{
              left: triggerRect ? triggerRect.left : '50%',
              top: triggerRect ? triggerRect.bottom + 8 : '50%',
              transformOrigin: 'top left',
            }}
            initial={{ opacity: 0, scale: 0.9, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: -8 }}
          >
            <div className="px-4 py-3 font-medium text-foreground">{title}</div>
            {children}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export function FloatingActionPanelButton({ children, onClick, className }) {
  return (
    <button
      type="button"
      className={cn(
        'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-foreground transition-colors',
        'hover:bg-gray-100 focus:bg-gray-100 focus:outline-none',
        className
      )}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export function FloatingActionPanelForm({ children, onSubmit, className }) {
  const context = React.useContext(FloatingActionPanelContext);
  if (!context) throw new Error('FloatingActionPanelForm must be inside FloatingActionPanelRoot');
  const { note, closePanel } = context;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit?.(note);
    closePanel();
  };

  return (
    <form className={cn('flex h-full flex-col', className)} onSubmit={handleSubmit}>
      {children}
    </form>
  );
}

export function FloatingActionPanelTextarea({ className, id }) {
  const context = React.useContext(FloatingActionPanelContext);
  if (!context) throw new Error('FloatingActionPanelTextarea must be inside FloatingActionPanelRoot');
  const { note, setNote } = context;

  return (
    <textarea
      id={id}
      className={cn('h-full w-full resize-none rounded-lg bg-transparent px-4 py-3 text-sm outline-none', className)}
      autoFocus
      value={note}
      onChange={(e) => setNote(e.target.value)}
    />
  );
}
